import { useEffect, useRef, useState } from "react";
import { useIsFetching, useQueryClient } from "@tanstack/react-query";
import { usePwaMode } from "../hooks/usePwaMode";

const DOUBLE_TAP_MS = 600;
const SCROLL_TO_TOP_THRESHOLD = 200;

export function PwaAffordances() {
  const isPwa = usePwaMode();
  if (!isPwa) return null;
  return (
    <>
      <RefreshButton />
      <ScrollToTopButton />
    </>
  );
}

function RefreshButton() {
  const qc = useQueryClient();
  const isFetching = useIsFetching() > 0;
  const lastTap = useRef(0);

  const onTap = () => {
    const now = Date.now();
    if (now - lastTap.current < DOUBLE_TAP_MS) {
      // Second tap inside the window → hard reload to pick up a new SPA
      // bundle if one's been deployed.
      window.location.reload();
      return;
    }
    lastTap.current = now;
    void qc.invalidateQueries();
  };

  return (
    <button
      type="button"
      onClick={onTap}
      aria-label="Refresh"
      className="fixed right-3 z-30 flex h-9 w-9 items-center justify-center rounded-full bg-slate-800/90 text-slate-100 shadow-lg backdrop-blur ring-1 ring-slate-700 active:scale-95 transition"
      style={{ top: "calc(env(safe-area-inset-top) + 0.5rem)" }}
    >
      {isFetching ? <Spinner /> : <RefreshIcon />}
    </button>
  );
}

function ScrollToTopButton() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > SCROLL_TO_TOP_THRESHOLD);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (!visible) return null;

  return (
    <button
      type="button"
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      aria-label="Scroll to top"
      className="fixed z-30 flex h-11 w-11 items-center justify-center rounded-full bg-blue-600 text-white shadow-xl ring-1 ring-blue-500/40 active:scale-95 transition"
      style={{
        bottom: "calc(env(safe-area-inset-bottom) + 1rem)",
        right: "calc(env(safe-area-inset-right) + 1rem)",
      }}
    >
      <ArrowUpIcon />
    </button>
  );
}

function RefreshIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 12a9 9 0 1 1-3-6.7" />
      <path d="M21 4v5h-5" />
    </svg>
  );
}

function Spinner() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="animate-spin"
    >
      <path d="M21 12a9 9 0 1 1-3-6.7" />
    </svg>
  );
}

function ArrowUpIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 19V5" />
      <path d="m5 12 7-7 7 7" />
    </svg>
  );
}
